/**
 * URL-persisted screen state.
 *
 * Contract every screen owes (see the Task 38 URL-state audit):
 *   - navigable state (tab, unit filter, search text, page, sort) lives in the
 *     query string, so a refresh, a bookmark, a shared link and the browser
 *     back button all restore the same view;
 *   - the URL wins over app defaults — if a param is present it is used as-is
 *     and a later data load must not overwrite it;
 *   - defaults are *omitted* from the query string so a pristine screen has a
 *     clean URL;
 *   - parsing is total: a hostile or stale value falls back to the default
 *     instead of throwing or rendering an empty screen.
 *
 * Only `URLSearchParams` is used — no bespoke query-string parsing anywhere.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/** Trimmed free text, or the fallback when absent/blank. */
export function readTextParam(params: URLSearchParams, key: string, fallback = ''): string {
  const raw = params.get(key);
  if (raw === null) {
    return fallback;
  }
  const trimmed = raw.trim();
  return trimmed === '' ? fallback : trimmed;
}

/** One of `allowed`, or the fallback. Matching is case-insensitive. */
export function readEnumParam<T extends string>(
  params: URLSearchParams,
  key: string,
  allowed: readonly T[],
  fallback: T
): T {
  const raw = params.get(key);
  if (raw === null) {
    return fallback;
  }
  const needle = raw.trim().toLowerCase();
  return allowed.find((value) => value.toLowerCase() === needle) ?? fallback;
}

export interface IntParamOptions {
  fallback: number;
  min?: number;
  max?: number;
}

/** A finite integer clamped into `[min, max]`, or the fallback. */
export function readIntParam(
  params: URLSearchParams,
  key: string,
  options: IntParamOptions
): number {
  const raw = params.get(key);
  if (raw === null || raw.trim() === '') {
    return options.fallback;
  }
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
    return options.fallback;
  }
  const min = options.min ?? Number.NEGATIVE_INFINITY;
  const max = options.max ?? Number.POSITIVE_INFINITY;
  return Math.min(Math.max(parsed, min), max);
}

/** `1`/`true`/`on`/`yes` → true; `0`/`false`/`off`/`no` → false; else fallback. */
export function readBoolParam(params: URLSearchParams, key: string, fallback = false): boolean {
  const raw = params.get(key);
  if (raw === null) {
    return fallback;
  }
  const needle = raw.trim().toLowerCase();
  if (['1', 'true', 'on', 'yes'].includes(needle)) return true;
  if (['0', 'false', 'off', 'no'].includes(needle)) return false;
  return fallback;
}

/**
 * Apply a patch to an existing query string. `null`/`undefined`/`''` delete the
 * key. Returns a new instance — never mutates the caller's params.
 */
export function patchParams(
  params: URLSearchParams,
  patch: Record<string, string | number | boolean | null | undefined>
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const [key, value] of Object.entries(patch)) {
    if (value === null || value === undefined || value === '') {
      next.delete(key);
    } else {
      next.set(key, String(value));
    }
  }
  return next;
}

/** `/roster` + `{ q: 'ri co', page: 2 }` → `/roster?q=ri+co&page=2` (encoded). */
export function buildDeepLink(
  pathname: string,
  params?: Record<string, string | number | boolean | null | undefined> | URLSearchParams
): string {
  const search =
    params instanceof URLSearchParams ? params : patchParams(new URLSearchParams(), params ?? {});
  const query = search.toString();
  return query === '' ? pathname : `${pathname}?${query}`;
}

/* ------------------------------------------------------------------ specs */

/**
 * A declarative description of one query parameter. `read` parses, `serialize`
 * renders back to a string, and `isDefault` decides whether the key is dropped
 * so default views keep a clean URL.
 */
export interface UrlParamSpec<TValue> {
  key: string;
  read: (params: URLSearchParams) => TValue;
  serialize: (value: TValue) => string;
  isDefault: (value: TValue) => boolean;
}

export function textParam(key: string, fallback = ''): UrlParamSpec<string> {
  return {
    key,
    read: (params) => readTextParam(params, key, fallback),
    serialize: (value) => value,
    isDefault: (value) => value === fallback,
  };
}

export function enumParam<T extends string>(
  key: string,
  allowed: readonly T[],
  fallback: T
): UrlParamSpec<T> {
  return {
    key,
    read: (params) => readEnumParam(params, key, allowed, fallback),
    serialize: (value) => value,
    isDefault: (value) => value === fallback,
  };
}

export function intParam(key: string, options: IntParamOptions): UrlParamSpec<number> {
  return {
    key,
    read: (params) => readIntParam(params, key, options),
    serialize: (value) => String(value),
    isDefault: (value) => value === options.fallback,
  };
}

/**
 * An integer restricted to a whitelist (e.g. `limit` ∈ PAGE_SIZE_OPTIONS) —
 * anything else, including hostile input, falls back.
 */
export function intChoiceParam(
  key: string,
  allowed: readonly number[],
  fallback: number
): UrlParamSpec<number> {
  return {
    key,
    read: (params) => {
      const raw = params.get(key);
      if (raw === null || raw.trim() === '') return fallback;
      const parsed = Number(raw);
      return allowed.includes(parsed) ? parsed : fallback;
    },
    serialize: (value) => String(value),
    isDefault: (value) => value === fallback,
  };
}

export function boolParam(key: string, fallback = false): UrlParamSpec<boolean> {
  return {
    key,
    read: (params) => readBoolParam(params, key, fallback),
    serialize: (value) => (value ? '1' : '0'),
    isDefault: (value) => value === fallback,
  };
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/** justification: a spec map is heterogeneous by construction; values are narrowed by `UrlStateOf`. */
export type UrlStateSpec = Record<string, UrlParamSpec<any>>;
/* eslint-enable @typescript-eslint/no-explicit-any */

export type UrlStateOf<TSpec extends UrlStateSpec> = {
  [K in keyof TSpec]: TSpec[K] extends UrlParamSpec<infer V> ? V : never;
};

/** Parse a full screen state out of a query string. Total; never throws. */
export function readUrlState<TSpec extends UrlStateSpec>(
  spec: TSpec,
  params: URLSearchParams
): UrlStateOf<TSpec> {
  const state = {} as Record<string, unknown>;
  for (const [field, param] of Object.entries(spec)) {
    state[field] = param.read(params);
  }
  return state as UrlStateOf<TSpec>;
}

/**
 * Render a screen state back to a query string, preserving any unrelated keys
 * already present in `base` (e.g. a deep-linked `play_id`).
 */
export function writeUrlState<TSpec extends UrlStateSpec>(
  spec: TSpec,
  state: UrlStateOf<TSpec>,
  base?: URLSearchParams
): URLSearchParams {
  const next = new URLSearchParams(base);
  for (const [field, param] of Object.entries(spec)) {
    const value = (state as Record<string, unknown>)[field];
    if (param.isDefault(value)) {
      next.delete(param.key);
    } else {
      next.set(param.key, param.serialize(value));
    }
  }
  return next;
}

export interface UrlStateHandle<TSpec extends UrlStateSpec> {
  /** Current state, parsed from the live query string. */
  state: UrlStateOf<TSpec>;
  /** Merge a patch into the URL. Uses `replace` so filtering does not stack history entries. */
  setState: (patch: Partial<UrlStateOf<TSpec>>, options?: { push?: boolean }) => void;
  /** Reset every spec'd key back to its default (leaves unrelated keys alone). */
  reset: () => void;
  /** The query string as it stands, for building share links. */
  search: URLSearchParams;
}

/**
 * React binding: `const { state, setState } = useUrlState(ARCHIVE_URL_STATE)`.
 *
 * Filter changes use `replace: true` so a user tweaking three filters does not
 * have to press Back three times; pass `{ push: true }` for state changes that
 * should be individually undoable (tab switches, pagination).
 */
export function useUrlState<TSpec extends UrlStateSpec>(spec: TSpec): UrlStateHandle<TSpec> {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => readUrlState(spec, params), [spec, params]);

  const setState = useCallback(
    (patch: Partial<UrlStateOf<TSpec>>, options?: { push?: boolean }) => {
      const next = { ...readUrlState(spec, params), ...patch };
      setParams(writeUrlState(spec, next, params), { replace: options?.push !== true });
    },
    [spec, params, setParams]
  );

  const reset = useCallback(() => {
    const cleared = new URLSearchParams(params);
    for (const param of Object.values(spec)) {
      cleared.delete(param.key);
    }
    setParams(cleared, { replace: true });
  }, [spec, params, setParams]);

  return { state, setState, reset, search: params };
}

/* -------------------------------------------------- shared param vocabulary */

export const UNIT_FILTER_VALUES = ['ALL', 'OFFENSE', 'DEFENSE', 'SPECIAL_TEAMS'] as const;
export type UnitFilterValue = (typeof UNIT_FILTER_VALUES)[number];

export const CONFLICT_FILTER_VALUES = ['all', 'unresolved', 'resolved'] as const;
export type ConflictFilterValue = (typeof CONFLICT_FILTER_VALUES)[number];

/** Canonical query keys. Screens must not invent synonyms for these. */
export const URL_PARAM_KEYS = {
  unit: 'unit',
  conflictStatus: 'conflict_status',
  search: 'q',
  page: 'page',
  pageSize: 'limit',
  sortBy: 'sort_by',
  sortOrder: 'sort_order',
  tab: 'tab',
  section: 'section',
  season: 'season',
  cohort: 'cohort',
  slug: 'slug',
} as const;
